import { useState } from 'react'
import { DataTable, DataTableProps, DataTableRowEvent, DataTableValue } from 'primereact/datatable'

const EXPANDED_CLASS_NAME = 'expandable-table__row--expanded'
const TABLE_ROW_SELECTOR = '.expandable-table tr.expandable-table__row'

/**
 * Props are declared as a type instead of an interface,
 *  because DataTableProps is a union type, and cannot be extended.
 * This prop type is not entirely exhaustive,
 *  but adding an Omit<> wrapper to remove row control props
 *  e.g. onRowCollapse, onRowExpand, etc. seems a bit overkill.
 * Those props will be ignored. See implemnentation.
 */
type ExpandableTableProps<T extends ExpandableTableValue> = DataTableProps<T[]>
type ExpandableTableValue = DataTableValue & { id: string }

/**
 * This component makes folding table rows easier to reuse.
 * While the component is simple to use and performs
 *  like a regular data table with some extra sugar,
 *  the implementation is a bit fishy.
 * Because of how primereact exposes table rows (it doesn't at all)
 *  we query them by selector, so we can style adjecent rows when they open.
 * This mechanism is purely necessary for reasons pertaining to styling.
 */
export default function ExpandableTable<T extends ExpandableTableValue>({
    value,
    ...props
}: ExpandableTableProps<T>) {
    const [expandedRows, setExpandedRows] = useState<T[]>([])

    const queryRow = (object: T) => {
        if (!value) return
        const index = value.findIndex((v) => v.id === object.id)
        if (!~index) return
        const rows = document.querySelectorAll(TABLE_ROW_SELECTOR)
        return rows[index]
    }

    const expandRow = (event: DataTableRowEvent) =>
        queryRow(event.data as T)?.classList.add(EXPANDED_CLASS_NAME)

    const collapseRow = (event: DataTableRowEvent) =>
        queryRow(event.data as T)?.classList.remove(EXPANDED_CLASS_NAME)

    // Let primereact control the expanded state by using toggle instead of using add/remove approach.
    const toggleRow = (event: DataTableRowEvent) => setExpandedRows(event.data as T[])

    return (
        <DataTable
            {...props}
            className="expandable-table"
            pt={{
                bodyRow: { className: 'expandable-table__row' },
                rowExpansion: { className: 'expandable-table__expansion' },
            }}
            onRowToggle={toggleRow}
            onRowExpand={expandRow}
            onRowCollapse={collapseRow}
            expandedRows={expandedRows}
            value={value}
        />
    )
}
